import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import useAxiosClient from "../../../axios-client";

/**
 * Fabric Patterns — the one reference list that is not just names.
 *
 * A pattern belongs to a fabric, so this pane is scoped to one at a time rather
 * than listing everything: the prints of a single fabric already run to
 * hundreds. And a print is chosen by *looking* at it, so each row carries its
 * swatch and a new pattern can be given one on the way in.
 *
 * That scope is the pane's own quirk, so it is a control, not a sentence: a
 * segmented picker sits directly under the header carrying each fabric's count,
 * opened on the busiest one. Everything else — the header, the docked write
 * strip, the single scroll region — is deliberately the same shape as
 * [ReferenceListPane], so the seven lists read as one page.
 */
const MAX_SWATCH_BYTES = 5 * 1024 * 1024;

export default function FabricPatternsPane({
  fabricTypes = [],
  fabricTypesLoading = false,
  search = "",
  onSearchChange,
  searchInputRef,
  onTotalChange,
}) {
  const axiosClient = useAxiosClient();

  const [fabricTypeId, setFabricTypeId] = useState(null);
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [counts, setCounts] = useState({});
  const [countsReady, setCountsReady] = useState(false);

  // One write at a time, docked above the scroller — see ReferenceListPane for
  // why these never render inline in the row.
  const [write, setWrite] = useState(null); // {mode:'create'|'rename'|'delete', id?, value?, file?}
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const writeInputRef = useRef(null);

  const closeWrite = useCallback(() => {
    setWrite(null);
    setError(null);
    setSaving(false);
  }, []);

  // Open on the fabric that actually has patterns, not on whichever happens to
  // be first. An imported book of hundreds usually sits under a fabric added
  // late, so "first" reliably shows an empty-looking list and hides the
  // catalogue — which is exactly how this list was first read as broken.
  useEffect(() => {
    if (fabricTypeId !== null || fabricTypes.length === 0 || !countsReady) return;

    const busiest = fabricTypes.reduce(
      (best, f) => ((counts[f.id] || 0) > (counts[best.id] || 0) ? f : best),
      fabricTypes[0],
    );
    setFabricTypeId(busiest.id);
  }, [fabricTypes, fabricTypeId, counts, countsReady]);

  const errorText = (err, fallback) =>
    (err.response && err.response.data && err.response.data.message)
    || err.message || fallback;

  const fetchCounts = useCallback(async () => {
    try {
      const res = await axiosClient.get('/settings/fabricPatterns/counts');
      setCounts(res.data.data || {});
    } catch (error) {
      // A failed count only costs the pane its labels and its opening fabric;
      // it must not stop the list below from working.
      setCounts({});
    } finally {
      setCountsReady(true);
    }
  }, [axiosClient]);

  useEffect(() => { fetchCounts(); }, [fetchCounts]);

  // The rail names every list with a count, and patterns are the one list whose
  // total lives here rather than in a hook the page can read.
  const grandTotal = useMemo(
    () => Object.values(counts).reduce((sum, n) => sum + (Number(n) || 0), 0),
    [counts]
  );

  useEffect(() => {
    if (countsReady && onTotalChange) onTotalChange(grandTotal);
  }, [grandTotal, countsReady, onTotalChange]);

  const fetchPatterns = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await axiosClient.get('/settings/fabricPatterns/index', {
        params: { fabricType: id },
      });
      setPatterns(res.data.data || []);
    } catch (error) {
      toast.error(errorText(error, 'Failed to load patterns'));
      setPatterns([]);
    } finally {
      setLoading(false);
    }
  }, [axiosClient]);

  useEffect(() => { fetchPatterns(fabricTypeId); }, [fabricTypeId, fetchPatterns]);

  useEffect(() => {
    if (write?.mode === "create" || write?.mode === "rename") {
      writeInputRef.current?.focus();
      writeInputRef.current?.select();
    }
  }, [write?.mode, write?.id]);

  const firstValidationError = (error) => {
    const errors = error.response?.data?.errors;
    if (!errors) return null;
    const first = Object.values(errors)[0];
    return Array.isArray(first) ? first[0] : first;
  };

  const pickFile = (file) => {
    if (!file) return null;
    if (file.size > MAX_SWATCH_BYTES) {
      setError('That image is over 5MB.');
      return null;
    }
    setError(null);
    return file;
  };

  // -- writes ------------------------------------------------------------------
  const submitCreate = async () => {
    const name = (write?.value || "").trim();
    if (!name) {
      setError('Give the pattern a name.');
      return;
    }
    if (patterns.some((p) => p.name.toLowerCase() === name.toLowerCase())) {
      setError('This fabric already has a pattern with that name.');
      return;
    }

    const body = new FormData();
    body.append('fabricType', fabricTypeId);
    body.append('name', name);
    if (write.file) body.append('image', write.file);

    setSaving(true);
    try {
      const res = await axiosClient.post('/settings/fabricPatterns/store', body);
      toast.success(res.data.message);
      closeWrite();
      await Promise.all([fetchPatterns(fabricTypeId), fetchCounts()]);
    } catch (error) {
      setError(firstValidationError(error) || errorText(error, 'Failed to create'));
      setSaving(false);
    }
  };

  const submitRename = async () => {
    const name = (write?.value || "").trim();
    const pattern = patterns.find((p) => p.id === write.id);
    if (!name || name === pattern?.name) return closeWrite();

    const body = new FormData();
    body.append('name', name);

    setSaving(true);
    try {
      const res = await axiosClient.post(`/settings/fabricPatterns/update/${pattern.id}`, body);
      toast.success(res.data.message);
      closeWrite();
      await fetchPatterns(fabricTypeId);
    } catch (error) {
      setError(firstValidationError(error) || errorText(error, 'Failed to rename'));
      setSaving(false);
    }
  };

  const replaceSwatch = async (pattern, file) => {
    if (!file) return;
    if (file.size > MAX_SWATCH_BYTES) {
      toast.error('That image is over 5MB.');
      return;
    }

    const body = new FormData();
    body.append('name', pattern.name);
    body.append('image', file);

    try {
      const res = await axiosClient.post(`/settings/fabricPatterns/update/${pattern.id}`, body);
      toast.success(res.data.message);
      await fetchPatterns(fabricTypeId);
    } catch (error) {
      toast.error(firstValidationError(error) || errorText(error, 'Failed to replace swatch'));
    }
  };

  const submitDelete = async () => {
    setError(null);
    setSaving(true);
    try {
      const res = await axiosClient.delete(`/settings/fabricPatterns/delete/${write.id}`);
      toast.success(res.data.message);
      closeWrite();
      await Promise.all([fetchPatterns(fabricTypeId), fetchCounts()]);
    } catch (error) {
      // The server's refusal is the useful part — it knows what still uses the
      // pattern — so it is shown in the strip rather than toasted away.
      setError(errorText(error, 'Failed to delete'));
      setSaving(false);
    }
  };

  const onWriteKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      write.mode === 'create' ? submitCreate() : submitRename();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeWrite();
    }
  };

  // -- derived -----------------------------------------------------------------
  const ft = search.trim().toLowerCase();
  const shown = ft ? patterns.filter((p) => p.name.toLowerCase().includes(ft)) : patterns;
  const fabricName = fabricTypes.find((f) => f.id === fabricTypeId)?.name || '';
  const target = write?.id ? patterns.find((p) => p.id === write.id) : null;

  const countText = fabricName
    ? `${(ft ? shown.length : patterns.length).toLocaleString()} in ${fabricName}`
    : `${patterns.length}`;

  return (
    <section className="ps-pane">
      {/* ---- header ---- */}
      <header className="ps-pane-head">
        <div className="ps-pane-title">
          <span className="ps-pane-name">Fabric Patterns</span>
          <span className="ps-pane-count">{countText}</span>
        </div>

        <div className="ps-pane-tools">
          <div className={`ps-search ${search ? "ps-search-active" : ""}`}>
            <span className="ps-search-icon" aria-hidden="true" />
            <input
              ref={searchInputRef}
              className="ps-search-input"
              type="text"
              placeholder={fabricName ? `Search patterns in ${fabricName}` : 'Search patterns'}
              value={search}
              onChange={(e) => onSearchChange(e.target.value)}
            />
            {!search && <span className="ps-search-kbd">/</span>}
          </div>

          <button
            type="button"
            className="ps-btn-primary"
            disabled={!fabricTypeId}
            title={fabricTypeId ? 'Add a pattern' : 'Pick a fabric first'}
            onClick={() => setWrite({ mode: 'create', value: '', file: null })}
          >
            Add pattern
          </button>
        </div>
      </header>

      {/* ---- scope: a pattern only means anything beside its fabric ---- */}
      <div className="ps-scope">
        <span className="ps-scope-label">Fabric type</span>
        <div className="ps-scope-chips">
          {fabricTypesLoading && fabricTypes.length === 0 ? (
            <span className="ps-scope-loading">Loading fabrics…</span>
          ) : (
            fabricTypes.map((f) => (
              <button
                type="button"
                key={f.id}
                role="radio"
                aria-checked={f.id === fabricTypeId}
                className={`ps-chip ${f.id === fabricTypeId ? 'ps-chip-active' : ''}`}
                onClick={() => { setFabricTypeId(f.id); closeWrite(); }}
              >
                {f.name}
                {/* A bare "(0)" is noise — an empty fabric just shows its name. */}
                {counts[f.id] ? <span className="ps-chip-count">{counts[f.id]}</span> : null}
              </button>
            ))
          )}
        </div>
      </div>

      {/* ---- docked write strip ---- */}
      {write?.mode === 'create' && (
        <div className="ps-strip">
          <div className="ps-strip-label">
            New pattern{fabricName ? ` · ${fabricName}` : ''}
          </div>
          <div className="ps-strip-row">
            <input
              ref={writeInputRef}
              className={`ps-strip-input ${error ? 'ps-strip-input-error' : ''}`}
              placeholder="Pattern name"
              value={write.value}
              onChange={(e) => { setWrite({ ...write, value: e.target.value }); setError(null); }}
              onKeyDown={onWriteKeyDown}
            />
            <label className="ps-btn-quiet ps-file-btn">
              {write.file ? write.file.name : 'Swatch…'}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) => setWrite({ ...write, file: pickFile(e.target.files?.[0]) })}
              />
            </label>
            <button type="button" className="ps-btn-primary ps-btn-sm" disabled={saving} onClick={submitCreate}>
              {saving ? 'Saving' : 'Save'}
              {saving ? <span className="ps-spinner" /> : <span className="ps-kbd">⏎</span>}
            </button>
            <button type="button" className="ps-btn-quiet" onClick={closeWrite}>Cancel</button>
          </div>
          {error && <div className="ps-strip-error">{error}</div>}
        </div>
      )}

      {write?.mode === 'rename' && (
        <div className="ps-strip">
          <div className="ps-strip-label">Renaming · {target?.name}</div>
          <div className="ps-strip-row">
            <input
              ref={writeInputRef}
              className={`ps-strip-input ps-strip-input-focus ${error ? 'ps-strip-input-error' : ''}`}
              value={write.value}
              onChange={(e) => { setWrite({ ...write, value: e.target.value }); setError(null); }}
              onKeyDown={onWriteKeyDown}
            />
            <button type="button" className="ps-btn-primary ps-btn-sm" disabled={saving} onClick={submitRename}>
              {saving ? 'Saving' : 'Save'}
              {saving ? <span className="ps-spinner" /> : <span className="ps-kbd">⏎</span>}
            </button>
            <button type="button" className="ps-btn-quiet" onClick={closeWrite}>Esc</button>
          </div>
          {error && <div className="ps-strip-error">{error}</div>}
        </div>
      )}

      {write?.mode === 'delete' && (
        <div className="ps-strip ps-strip-danger">
          <div className="ps-strip-title">Delete “{target?.name}”?</div>
          <div className="ps-strip-body">
            It is removed from {fabricName || 'this fabric'} and from every picker
            that offers it. Orders already placed keep their recorded pattern.
          </div>
          <div className="ps-strip-row">
            <button type="button" className="ps-btn-danger" disabled={saving} onClick={submitDelete}>
              {saving ? 'Deleting…' : 'Delete pattern'}
            </button>
            <button type="button" className="ps-btn-quiet" onClick={closeWrite}>Keep it</button>
          </div>
          {error && (
            <div className="ps-strip-refusal">
              <span className="ps-strip-refusal-dot" aria-hidden="true" />
              <span>{error}</span>
            </div>
          )}
        </div>
      )}

      {/* ---- the one scroll region ---- */}
      <div className={`ps-scroll ${write ? 'ps-scroll-dimmed' : ''}`}>
        {loading && (
          <div className="ps-skeleton" aria-hidden="true">
            {[38, 52, 29, 44, 34].map((w, i) => (
              <div className="ps-skeleton-row ps-skeleton-row-swatch" key={i}>
                <span className="ps-skeleton-tile" />
                <span className="ps-skeleton-bar" style={{ width: `${w}%` }} />
              </div>
            ))}
          </div>
        )}

        {!loading && patterns.length === 0 && !ft && (
          <div className="ps-blank">
            <span className="ps-blank-mark" aria-hidden="true" />
            <div className="ps-blank-title">
              No patterns in {fabricName || 'this fabric'} yet
            </div>
            <div className="ps-blank-body">
              Patterns are the prints a fabric comes in. Each can carry a swatch,
              so it can be picked by eye on the order form.
            </div>
            <button
              type="button"
              className="ps-btn-primary"
              disabled={!fabricTypeId}
              onClick={() => setWrite({ mode: 'create', value: '', file: null })}
            >
              Add the first pattern
            </button>
          </div>
        )}

        {!loading && shown.length === 0 && ft && (
          <div className="ps-blank">
            <div className="ps-blank-title">Nothing matches “{search}”</div>
            <div className="ps-blank-body">
              {patterns.length} {patterns.length === 1 ? 'pattern' : 'patterns'} in{' '}
              {fabricName}. Clear the search to see them all.
            </div>
            <div className="ps-blank-actions">
              <button type="button" className="ps-btn-quiet" onClick={() => onSearchChange('')}>
                Clear search
              </button>
              <button
                type="button"
                className="ps-btn-primary"
                onClick={() => setWrite({ mode: 'create', value: search.trim(), file: null })}
              >
                Add “{search.trim()}”
              </button>
            </div>
          </div>
        )}

        {!loading &&
          shown.map((pattern) => {
            const isTarget = write?.id === pattern.id;
            return (
              <div key={pattern.id} className={`ps-row ps-row-swatch ${isTarget ? 'ps-row-target' : ''}`}>
                {pattern.url ? (
                  <img className="ps-swatch" src={pattern.url} alt="" loading="lazy" />
                ) : (
                  <span className="ps-swatch ps-swatch-empty" aria-label="no swatch">
                    no<br />swatch
                  </span>
                )}

                <span className="ps-row-name" title={pattern.name}>
                  {ft ? <HighlightMatch text={pattern.name} query={ft} /> : pattern.name}
                </span>

                {isTarget ? (
                  <span className="ps-row-state">
                    {write.mode === 'rename' ? 'being renamed' : 'being deleted'}
                  </span>
                ) : (
                  <>
                    {/* Adding the missing picture is the more useful of the two,
                        so an empty swatch says so in the accent colour. */}
                    <label
                      className={`ps-row-action ${pattern.url ? '' : 'ps-row-action-accent'}`}
                      title={pattern.url ? 'Replace swatch' : 'Add swatch'}
                    >
                      {pattern.url ? 'Replace swatch' : 'Add swatch'}
                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={(e) => replaceSwatch(pattern, e.target.files?.[0])}
                      />
                    </label>
                    <button
                      type="button"
                      className="ps-row-action"
                      onClick={() => setWrite({ mode: 'rename', id: pattern.id, value: pattern.name })}
                    >
                      Rename
                    </button>
                    <button
                      type="button"
                      className="ps-row-action ps-row-action-danger"
                      onClick={() => setWrite({ mode: 'delete', id: pattern.id })}
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>
            );
          })}
      </div>
    </section>
  );
}

/** Highlights the first occurrence of `query` inside `text`. */
function HighlightMatch({ text, query }) {
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="ps-mark">{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}
